'use client';

import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const openingHours = [
  { day: 'Lundi', hours: '10:00 - 22:30' },
  { day: 'Mardi', hours: '10:00 - 22:30' },
  { day: 'Mercredi', hours: '10:00 - 22:30' },
  { day: 'Jeudi', hours: '10:00 - 22:30' },
  { day: 'Vendredi', hours: '10:00 - 22:30' },
  { day: 'Samedi', hours: '10:00 - 22:30' },
  { day: 'Dimanche', hours: 'Fermé' },
];

export default function ContactPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      {/* Page Header */}
      <section className="relative overflow-hidden bg-card py-20 border-b border-border/40">
        <div className="container mx-auto px-4 text-center">
          <motion.h1
            className="font-serif text-5xl md:text-6xl font-bold tracking-tight mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Contactez-nous
          </motion.h1>
          <motion.p
            className="text-lg text-muted-foreground max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Nous serions ravis de vous accueillir chez Crudo e Vino
          </motion.p>
        </div>
      </section>

      {/* Contact Information Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {/* Address */}
            <motion.div
              {...fadeInUp}
              className="bg-card border border-border/40 rounded-lg p-8"
            >
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                <MapPin className="w-7 h-7 text-primary" />
              </div>
              <h2 className="font-serif text-2xl font-semibold mb-4">Adresse</h2>
              <div className="text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">Auchan Opkorn</p>
                <p>1 boulevard Émile Krieps</p>
                <p>L-4622 Differdange</p>
                <p>Luxembourg</p>
              </div>
              <div className="mt-6">
                <a
                  href="https://www.google.com/maps/search/?api=1&query=1+boulevard+%C3%89mile+Krieps+Differdange+Luxembourg"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-primary hover:underline"
                >
                  Ouvrir dans Google Maps →
                </a>
              </div>
            </motion.div>

            {/* Contact Details */}
            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.1 }}
              className="bg-card border border-border/40 rounded-lg p-8"
            >
              <div className="w-14 h-14 rounded-full bg-secondary/10 flex items-center justify-center mb-6">
                <Phone className="w-7 h-7 text-secondary" />
              </div>
              <h2 className="font-serif text-2xl font-semibold mb-4">Contact</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Téléphone</p>
                  <a
                    href="tel:+35227521531"
                    className="text-lg font-medium text-foreground hover:text-primary transition-colors"
                  >
                    +352 2 752 1531
                  </a>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Email</p>
                  <a
                    href="mailto:liminalux@outlook.com"
                    className="text-lg font-medium text-foreground hover:text-primary transition-colors"
                  >
                    liminalux@outlook.com
                  </a>
                </div>
                <div className="pt-4">
                  <p className="text-sm text-muted-foreground mb-2">Réservations</p>
                  <a
                    href="/reserver"
                    className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold text-primary-foreground bg-primary rounded-lg hover:opacity-90 transition-all hover:scale-105"
                  >
                    Réserver une Table
                  </a>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Opening Hours */}
          <motion.div
            {...fadeInUp}
            transition={{ delay: 0.2 }}
            className="max-w-6xl mx-auto"
          >
            <div className="bg-card border border-border/40 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 rounded-full bg-foreground/10 flex items-center justify-center">
                  <Clock className="w-7 h-7 text-foreground" />
                </div>
                <h2 className="font-serif text-2xl font-semibold">Horaires d&apos;ouverture</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3">
                {openingHours.map((schedule) => (
                  <div
                    key={schedule.day}
                    className="flex justify-between items-center py-3 border-b border-border/40 last:border-b-0"
                  >
                    <span className="font-medium">{schedule.day}</span>
                    <span className={schedule.hours === 'Fermé' ? 'text-muted-foreground italic' : 'text-foreground'}>
                      {schedule.hours}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-6 space-y-3">
                <div className="p-4 bg-secondary/10 rounded-lg border border-secondary/30">
                  <p className="text-sm text-center">
                    <span className="font-semibold text-secondary">Cuisine continue</span> toute la journée
                  </p>
                </div>
                <div className="p-4 bg-primary/10 rounded-lg border border-primary/30">
                  <p className="text-sm text-center">
                    <span className="font-semibold text-primary">Parking gratuit 2h</span> disponible à Auchan Opkorn
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-card border-t border-border/40">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <h2 className="font-serif text-4xl font-bold mb-4">
                Nous Trouver
              </h2>
              <p className="text-muted-foreground">
                Situé au cœur de Differdange avec parking gratuit et accès facile
              </p>
            </div>

            <div className="relative w-full h-[500px] rounded-lg overflow-hidden border border-border/40 shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2594.4!2d5.8927!3d49.5214!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDnCsDMxJzE3LjAiTiA1wrA1MycMzMzMiRQ!5e0!3m2!1sfr!2slu!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Crudo e Vino Location"
              />
            </div>

            <div className="mt-8 text-center">
              <p className="text-muted-foreground">
                <strong className="text-foreground">Parking gratuit 2h</strong> disponible à Auchan Opkorn
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
